import React, { useCallback, useEffect } from "react";
import styled from "styled-components";
import { Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { LoaderSpinner2 } from "components";
import { AppDispatch, useTypedSelector } from "store";
import { selectProductLineId } from "store/reducers/products/products.selectors";
import {
  clearCachedTablesAction,
  fetchTableSummariesAction,
} from "store/reducers/products/tables/tables.actions";
import { selectTableSummaries } from "store/reducers/products/tables/tables.selectors";
import { TableSummariesState } from "store/reducers/products/tables/tables.types";
import { TableSummary } from "types/tableSummary";
import { TableSummaryGrid } from "./TableSummaryGrid";
import { ProductDetailPanelTitle } from "../ProductDetailPanelHeader";

export type TableSummaryViewState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "empty" }
  | {
      readonly kind: "ready";
      readonly summaries: readonly TableSummary[];
      readonly isRefreshing?: boolean;
    };

function getTableSummaryViewState(
  summariesState: TableSummariesState
): TableSummaryViewState {
  const { status, data } = summariesState;

  if (status === "loading" || status === "idle") {
    if (data.length > 0) {
      return {
        kind: "ready",
        summaries: data,
        isRefreshing: status === "loading",
      };
    }

    return { kind: "loading" };
  }

  if (status === "error") {
    return { kind: "error" };
  }

  if (data.length === 0) {
    return { kind: "empty" };
  }

  return { kind: "ready", summaries: data };
}

export default function TableSummaryPanel() {
  const dispatch = useDispatch<AppDispatch>();
  const productLineId: string | undefined =
    useTypedSelector(selectProductLineId);
  const summariesState: TableSummariesState =
    useTypedSelector(selectTableSummaries);
  const viewState: TableSummaryViewState =
    getTableSummaryViewState(summariesState);

  const refreshTableSummaries = useCallback(() => {
    if (!productLineId) {
      return;
    }

    // Drop any full tables left from Rules Tables / prior expands so
    // opened detail panels always refetch.
    dispatch(clearCachedTablesAction());
    void dispatch(fetchTableSummariesAction());
  }, [dispatch, productLineId]);

  useEffect(() => {
    refreshTableSummaries();
  }, [refreshTableSummaries]);

  return (
    <Wrapper>
      <ProductDetailPanelTitle>Table Summary</ProductDetailPanelTitle>
      <BodyArea>
        <TableSummaryContent
          viewState={viewState}
          onRefresh={refreshTableSummaries}
        />
      </BodyArea>
    </Wrapper>
  );
}

function TableSummaryContent({
  viewState,
  onRefresh,
}: {
  readonly viewState: TableSummaryViewState;
  readonly onRefresh: () => void;
}) {
  switch (viewState.kind) {
    case "loading":
      return (
        <LoaderWrapper>
          <LoaderSpinner2 />
        </LoaderWrapper>
      );

    case "error":
      return (
        <MessageWrapper>
          <Alert severity="error">
            Failed to load table summaries. Please try again.
          </Alert>
        </MessageWrapper>
      );

    case "empty":
      return (
        <MessageWrapper>
          <Alert severity="info">No tables found for this product line.</Alert>
        </MessageWrapper>
      );

    case "ready":
      return (
        <TableSummaryGrid
          summaries={viewState.summaries}
          onReloadData={onRefresh}
          isRefreshing={viewState.isRefreshing === true}
        />
      );

    default: {
      const _exhaustive: never = viewState;
      return _exhaustive;
    }
  }
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Fill ProductDetail PageContent — avoid calc(100vh) which overflows and
     creates an outer scrollbar that hides the grid footer. */
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  background-color: ${(props) => props.theme.colors.background.pageTable};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 48px;
`;

const MessageWrapper = styled.div`
  padding: 0 32px;
`;

const BodyArea = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-height: 0;
  height: 0;
  overflow: hidden;
`;
